import type { StepType } from '@reactour/tour'
import { TourProvider as BaseTourProvider } from '@reactour/tour'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  TripOnboardingEvents,
  tripOnboardingImpressionEvent,
} from 'analytics/eventDefinitions'
import {
  fireInteractionEvent,
  fireNonInteractionEvent,
} from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { useResponsive } from 'hooks/useResponsive'
import {
  setSeenStatus as setTourOnboardingSeenStatus,
  setSeenInThisSessionStatus,
} from 'tripPlanner/storage/onboardingTour'

const stepContent = (heading: string, body: string) => (
  <VerticalSpacer gap={8}>
    <Heading variant="heading6">{heading}</Heading>
    <BodyText variant="large">{body}</BodyText>
  </VerticalSpacer>
)

const styles: React.ComponentProps<typeof BaseTourProvider>['styles'] = {
  popover: (base) => ({
    ...base,
    borderRadius: rem(8),
    boxShadow:
      // Ideally we'd read this from the theme rather than copy/pasting it here, but that's a lot of hassle for one CSS property
      '0px 1px 5px rgba(0, 0, 0, 0.1), 0px 4px 14px rgba(0, 0, 0, 0.05), 0px 8px 24px rgba(0, 0, 0, 0.05), 0px 24px 35px rgba(0, 0, 0, 0.05)',
    padding: rem(20),
    paddingTop: rem(40),
  }),
  maskWrapper: (base) => ({
    ...base,
    opacity: 0.35,
  }),
}

const NavigationGroup = styled(Group)`
  margin-top: ${rem(16)};
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
`

const StepDisplay = styled(Caption)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(20)};
`

function TourNavigation({ currentStep, setCurrentStep, setIsOpen, steps }: {
  currentStep: number
  setCurrentStep: (step: number) => void
  setIsOpen: (open: boolean) => void
  steps: Array<StepType>
}) {
  const intl = useIntl()
  const isLast = currentStep === steps.length - 1
  const skip = () => {
    fireInteractionEvent(
      TripOnboardingEvents('dismiss', currentStep + 1, 'skip'),
    )
    setIsOpen(false)
  }
  const done = () => {
    fireInteractionEvent(
      TripOnboardingEvents('done_click', currentStep + 1),
    )
    setIsOpen(false)
  }
  const next = () => {
    fireInteractionEvent(
      TripOnboardingEvents('continue_click', currentStep + 1),
    )
    setCurrentStep(currentStep + 1)
  }

  return (<NavigationGroup
    direction="horizontal"
    horizontalAlign={isLast ? 'end' : 'space-between'}
  >
    {isLast && (
      <TextButton kind="tertiary" onClick={done} horizontalOutdent="end">
        {intl.formatMessage({
          id: 'qBR45BvCLC9Pu8WcnZpR',
          defaultMessage: 'Done',
          description: 'Button to finish the onboarding tour on the tour offer detail page',
        })}
      </TextButton>
    )}
    {!isLast && (
      <>
        <TextButton
          kind="tertiary"
          variant="dark"
          onClick={skip}
          horizontalOutdent="start"
        >
          {intl.formatMessage({
            id: 'UJlO45r_im53xL21F4GZ',
            defaultMessage: 'Skip',
            description: 'Button to skip the remaining onboarding tour steps',
          })}
        </TextButton>
        <TextButton kind="tertiary" onClick={next} horizontalOutdent="end">
          {intl.formatMessage({
            id: 'dO6Xoev6HGcYsd_SQR06',
            defaultMessage: 'Continue',
            description: 'Button to advance to the next onboarding tour step',
          })}
        </TextButton>
      </>
    )}
  </NavigationGroup>
  )
}

const tourComponents: React.ComponentProps<typeof BaseTourProvider>['components'] =
  {
    Close: ({ onClick }) => (
      <CloseButton kind="tertiary" onClick={onClick}>
        <LineTimesIcon colour="neutral-one" />
      </CloseButton>
    ),
    Badge: ({ children }: any) => (
      <StepDisplay variant="medium" colour="neutral-three">
        {children}
      </StepDisplay>
    ),
    Navigation: TourNavigation,
  }

const markSeen = () => {
  fireNonInteractionEvent(tripOnboardingImpressionEvent())
  setTourOnboardingSeenStatus(true)
  setSeenInThisSessionStatus(true)
}

interface Props {
  children: React.ReactNode
}

type TourProviderProps = React.ComponentProps<typeof BaseTourProvider>

function TourProvider({ children }: Props) {
  const intl = useIntl()
  const [match] = useResponsive()
  const isDesktopLayout = !!(match.desktop || match.largeDesktop)

  const onboardingSteps = useMemo<Array<StepType>>(() => [
    {
      selector: '.onboarding-tour-element-add-to-trip',
      resizeObservables: ['.onboarding-tour-element-add-to-trip'],
      mutationObservables: ['.onboarding-tour-element-add-to-trip'],
      content: stepContent(
        intl.formatMessage({
          id: 'vNx2o21yvyBxAKUtLN_E',
          defaultMessage: 'Add anything to your trip',
          description: 'Onboarding tour step heading for adding items to trip',
        }),
        intl.formatMessage({
          id: 'fGZsJ5tlQWeg5FSrtiDu',
          defaultMessage: 'Import your Luxury Escapes bookings or add anything else you want to do on your trip',
          description: 'Onboarding tour step body explaining trip item addition, including Luxury Escapes bookings import',
        }),
      ),
      padding: { mask: 8 },
    },
    {
      selector: '.onboarding-tour-element-map',
      resizeObservables: ['.onboarding-tour-element-map'],
      mutationObservables: ['.onboarding-tour-element-map'],
      content: stepContent(
        intl.formatMessage({
          id: 'kJ7ikJpcC6fb8STS1HhH',
          defaultMessage: 'Map out your journey',
          description: 'Onboarding tour step heading for the map view feature',
        }),
        intl.formatMessage({
          id: 'vFtFW2NPsXJN7imkrtFd',
          defaultMessage: 'Visualise your entire trip on a map with all your personalised trip items',
          description: 'Onboarding tour step body explaining the map visualisation feature',
        }),
      ),
      padding: { mask: 8 },
    },
    {
      selector: '.onboarding-tour-element-itinerary-tab',
      resizeObservables: ['.onboarding-tour-element-itinerary-tab'],
      mutationObservables: ['.onboarding-tour-element-itinerary-tab'],
      content: stepContent(
        intl.formatMessage({
          id: 's8EbeZqOGF0VhVxGDv9Z',
          defaultMessage: 'Day-by-day view',
          description: 'Onboarding tour step heading for the itinerary day view feature',
        }),
        intl.formatMessage({
          id: '5OEkUjfkZ9j4AnhaI6cG',
          defaultMessage: 'Once you add trip dates you’ll be able to view your trip as a day-by-day itinerary.',
          description: 'Onboarding tour step body explaining the day-by-day itinerary view',
        }),
      ),
      padding: { mask: 0 },
    },
  ], [intl])

  const onboardingStepsDesktop = useMemo<Array<StepType>>(() => [
    onboardingSteps[0],
    {
      ...onboardingSteps[1],
      position: 'center',
      padding: { mask: 0 },
    },
    onboardingSteps[2],
  ], [onboardingSteps])

  const steps = isDesktopLayout ? onboardingStepsDesktop : onboardingSteps

  const badgeContent = useCallback<NonNullable<TourProviderProps['badgeContent']>>((props) => {
    const currentStepNumber = props.currentStep + 1
    return intl.formatMessage(
      {
        id: '6s0hJGx4HwgEDpmizIC4',
        defaultMessage: '{currentStep} / {totalSteps}',
        description: 'Onboarding tour progress indicator showing current step out of total steps',
      },
      { currentStep: currentStepNumber, totalSteps: props.totalSteps },
    )
  }, [intl])

  const onClickClose: TourProviderProps['onClickClose'] = ({ currentStep, setIsOpen }) => {
    fireInteractionEvent(
      TripOnboardingEvents('dismiss', currentStep + 1, 'exit'),
    )
    setIsOpen(false)
  }
  const onClickMask: TourProviderProps['onClickMask'] = ({ currentStep, setIsOpen }) => {
    fireInteractionEvent(
      TripOnboardingEvents('dismiss', currentStep + 1, 'dismiss'),
    )
    setIsOpen(false)
  }

  return (
    <BaseTourProvider
      steps={steps}
      styles={styles}
      components={tourComponents}
      badgeContent={badgeContent}
      showDots={false}
      afterOpen={markSeen}
      onClickClose={onClickClose}
      onClickMask={onClickMask}
    >
      {children}
    </BaseTourProvider>
  )
}

export default TourProvider
